import { Modal } from '@/components/Elements';
import { CaretRight, Info, X } from '@phosphor-icons/react';
import HeaderDropdown from '@/features/reports/components/ViewDrillDowns/HeaderDropdown';
import Button from '@/components/Button';
import Tooltip from '@/components/Tooltip';
import { useEffect, useMemo, useState } from 'react';
import useGetMetricInfo from '@/features/reports/api/getMetricInfo';
import { twJoin, twMerge } from 'tailwind-merge';
import useGetMetricSampleData from '@/features/reports/api/getMetricSampleData';
import { ViewDrillDownsLoader } from '@/features/reports/loaders';
import Search from '@/features/reports/components/ViewDrillDowns/Search';
import Skeleton from '@/components/Skeleton';
import useGetSourcesInfo from '@/features/reports/api/getSourcesInfo';
import useIntegrationLogo from '@/features/reports/hooks/useIntegrationLogo';

export default function ViewDrillDowns({
  id,
  headerTitleTooltip,
  children,
  type = 'drilldown',
  headerTitle = 'Definitions',
  headerDropdownItems = [],
  showHeaderDropdownCount = false,
  modalClasses = {},
}) {
  const [open, setOpen] = useState(false);
  const [tableIndex, setTableIndex] = useState(0);
  const [columnIndex, setColumnIndex] = useState(0);
  const [selectedId, setSelectedId] = useState(id);

  const { isLoading: isMetricInfoLoading, data: metricInfoData } =
    useGetMetricInfo({
      metadata: {
        metricId: selectedId,
      },
      config: {
        enabled:
          open &&
          Boolean(selectedId) &&
          ['drilldown', 'sources'].includes(type),
      },
    });

  const { isLoading: isSourcesInfoLoading, data: sourceInfoData } =
    useGetSourcesInfo({
      metadata: {
        id: selectedId,
      },
      config: {
        enabled: open && Boolean(selectedId) && type === 'column-sources',
      },
    });

  const data = metricInfoData?.data || sourceInfoData?.data || {};
  const isLoading = isMetricInfoLoading || isSourcesInfoLoading;

  const tables = useMemo(() => {
    const tableData =
      type === 'drilldown'
        ? data?.drillDownSources
        : type === 'sources'
          ? data?.sources
          : data;
    return Array.isArray(tableData)
      ? tableData.map((table, tableIndex) => ({
          source: table.name,
          platform: table.platform,
          tableId: ['drilldown', 'sources'].includes(type)
            ? table.dictionaryId
            : table._id,
          tableName: table.name,
          tableLabel: table.label || '',
          tableIndex,
          columns: Array.isArray(table?.columns)
            ? table.columns.map((column, columnIndex) => ({
                tableName: table.name,
                columnName: column.name,
                columnLabel: column.label || '',
                description: column.description,
                connectionDictionaryId: column.connectionDictionaryId,
                connection: column.connection,
                columnIndex,
                tableIndex,
              }))
            : [],
        }))
      : [];
  }, [data]);

  const getLogo = useIntegrationLogo();

  const connectionDictionaryId =
    tables[tableIndex]?.columns?.[columnIndex]?.connectionDictionaryId;
  const connection =
    tables[tableIndex]?.columns?.[columnIndex]?.connection?.split?.('.')?.[1];

  const { isLoading: isSampleDataLoading, data: sampleData } =
    useGetMetricSampleData({
      metadata: {
        tableId: connectionDictionaryId || tables[tableIndex]?.tableId,
        columnName:
          connection || tables[tableIndex]?.columns?.[columnIndex]?.columnName,
      },
      config: {
        enabled:
          open &&
          Boolean(
            (tables[tableIndex]?.tableId &&
              tables[tableIndex]?.columns?.[columnIndex]?.columnName) ||
              (connectionDictionaryId && connection)
          ),
        staleTime: Infinity,
      },
    });

  useEffect(() => {
    const element = document.getElementById(`table-${tableIndex}`);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [tableIndex]);

  useEffect(() => {
    const element = document.getElementById(`column-${columnIndex}`);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [columnIndex]);

  return (
    <>
      {children({
        open,
        setOpen,
      })}
      <Modal
        isOpen={open}
        onClose={(e) => {
          e.stopPropagation();
          setOpen(false);
        }}
        topStripClassName="hidden"
        topBorderClassname="border-t-4 border-t-pv-primary-primary-500"
        containerClassname={twMerge(
          'w-9/12 min-h-[701px] max-w-[1178px]',
          twJoin(
            modalClasses?.containerClassname && modalClasses.containerClassname
          )
        )}
        childClassName="w-full h-full"
        className={twMerge(
          'w-full min-h-[701px] flex shadow-[0px_8px_24px_0px_rgba(144,144,144,0.10)]',
          twJoin(modalClasses?.className && modalClasses.className)
        )}
        showHeader={false}
        isMUIModal
        showBlackDrop={false}
        classes={{
          root: '!z-[11]',
        }}
      >
        <div className="w-full h-full">
          <div className="py-3 px-4 flex items-center">
            <div className="text-base text-pv-text-primary-text font-semibold leading-[26px]">
              {headerTitle}
            </div>
            {headerTitleTooltip && (
              <Tooltip title={headerTitleTooltip} placement="top">
                <Info
                  size={16}
                  className="text-pv-neutral-grey-600 hover:text-pv-neutral-grey-900 cursor-pointer ml-1.5"
                />
              </Tooltip>
            )}
            {headerDropdownItems.length > 0 && (
              <>
                <CaretRight
                  size={16}
                  className="text-pv-neutral-grey-400 mx-2"
                />
                <HeaderDropdown
                  selectedId={selectedId}
                  setSelectedId={setSelectedId}
                  items={headerDropdownItems}
                  showCount={showHeaderDropdownCount}
                />
              </>
            )}
            <Search
              tables={tables}
              setTableIndex={setTableIndex}
              setColumnIndex={setColumnIndex}
            />
            <Button
              className={{ button: { root: 'w-7 p-0 ml-3' } }}
              onClick={() => setOpen(false)}
            >
              <X size={16} className="text-pv-neutral-grey-400" />
            </Button>
          </div>
          {data.description && (
            <div className="my-2 mx-4 grid gap-1 text-xs leading-[19px]">
              {isLoading ? (
                <Skeleton
                  width="70%"
                  height="22px"
                  className="rounded-lg bg-pv-neutral-grey-100"
                />
              ) : (
                <>
                  <div className="text-pv-text-secondary-text font-medium">
                    Description
                  </div>
                  <Tooltip
                    title={data.description}
                    placement="top"
                    displayTooltipOnOverflow={true}
                  >
                    <p className="text-pv-text-primary-text line-clamp-2">
                      {data.description || ''}
                    </p>
                  </Tooltip>
                </>
              )}
            </div>
          )}
          <div className="h-[575px] mb-3 mx-4 grid gap-3 grid-cols-3">
            <div className="grid grid-rows-[auto_1fr] overflow-hidden">
              <div className="text-pv-text-secondary-text font-medium text-xs px-3 py-2.5">
                {type === 'drilldown' ? 'Drill-down Tables' : 'Source Tables'}
              </div>
              <div className="rounded-lg border border-pv-neutral-grey-100 py-2.5 pl-3 pr-1 overflow-hidden">
                {isLoading ? (
                  <ViewDrillDownsLoader />
                ) : (
                  <div className="h-full pr-[3px] overflow-y-scroll">
                    {tables.map((table) => (
                      <div
                        key={table.tableId}
                        id={`table-${table.tableIndex}`}
                        className={twMerge(
                          'flex items-center justify-between px-4 py-3 cursor-pointer border-l-3 border-l-transparent border-b border-b-pv-neutral-grey-100 hover:bg-pv-primary-primary-50 gap-2',
                          tableIndex === table.tableIndex &&
                            'border-l-brand-ai-100 font-medium'
                        )}
                        onClick={() => {
                          setTableIndex(table.tableIndex);
                          setColumnIndex(0);
                        }}
                      >
                        {type === 'column-sources' && (
                          <img
                            src={getLogo(table.platform).logo}
                            alt={getLogo(table.platform).name}
                            className="w-4 h-4"
                          />
                        )}
                        <Tooltip
                          title={table.tableLabel || table.tableName}
                          placement="top"
                          displayTooltipOnOverflow={true}
                        >
                          <div className="flex-1 truncate text-sm text-pv-text-primary-text leading-[22px]">
                            {table.tableLabel || table.tableName}
                          </div>
                        </Tooltip>
                        <CaretRight
                          size={16}
                          className={twMerge(
                            'text-pv-neutral-grey-400',
                            tableIndex === table.tableIndex &&
                              'text-pv-neutral-grey-600'
                          )}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-rows-[auto_1fr] overflow-hidden">
              <div className="text-pv-text-secondary-text font-medium text-xs px-3 py-2.5">
                {type === 'drilldown'
                  ? 'Drill-down Columns'
                  : `Columns for ${tables[tableIndex]?.tableLabel || 'Selected'} Table`}
              </div>
              <div className="rounded-lg border border-pv-neutral-grey-100 py-2.5 pl-3 pr-1 overflow-hidden">
                {isLoading ? (
                  <ViewDrillDownsLoader />
                ) : (
                  <div
                    className={twMerge(
                      'h-full pr-[3px] overflow-y-scroll',
                      !tables[tableIndex]?.columns?.length &&
                        'flex items-center justify-center'
                    )}
                  >
                    {tables[tableIndex]?.columns?.length > 0 ? (
                      <>
                        {tables[tableIndex].columns.map((column) => (
                          <div
                            key={`${column.tableId}-${column.columnName}`}
                            id={`column-${column.columnIndex}`}
                            className={twMerge(
                              'flex items-center p-3 cursor-pointer border-l-3 border-l-transparent border-b border-b-pv-neutral-grey-100 hover:bg-pv-primary-primary-50 gap-1',
                              columnIndex === column.columnIndex &&
                                'border-l-brand-ai-100 font-medium'
                            )}
                            onClick={() => setColumnIndex(column.columnIndex)}
                          >
                            <div className="flex items-center flex-1 gap-2 overflow-hidden">
                              <Tooltip
                                title={column.columnLabel}
                                placement="top"
                                displayTooltipOnOverflow={true}
                              >
                                <div className="inline-block truncate text-sm text-pv-text-primary-text leading-[22px]">
                                  {column.columnLabel}
                                </div>
                              </Tooltip>
                              <Tooltip
                                title={column.columnName}
                                placement="top"
                                displayTooltipOnOverflow={true}
                              >
                                <div className="w-fit max-w-[100px] bg-pv-primary-primary-100 rounded-lg px-2 py-0.5 text-pv-text-primary-text leading-[16px] text-[10px] truncate">
                                  {column.columnName}
                                </div>
                              </Tooltip>
                            </div>
                            <CaretRight
                              size={16}
                              className={twMerge(
                                'text-pv-neutral-grey-400',
                                columnIndex === column.columnIndex &&
                                  'text-pv-neutral-grey-600'
                              )}
                            />
                          </div>
                        ))}
                      </>
                    ) : (
                      <p className="text-pv-text-secondary-text text-sm">
                        No drill-downs available.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-rows-[auto_1fr] overflow-hidden">
              <div className="text-pv-text-secondary-text font-medium text-xs px-3 py-2.5">
                Column Information
              </div>
              <div
                className={twMerge(
                  'grid grid-rows-[auto_1fr] overflow-hidden rounded-lg border border-pv-neutral-grey-100',
                  !tables[tableIndex]?.columns?.length &&
                    'flex items-center justify-center'
                )}
              >
                {tables[tableIndex]?.columns?.length > 0 ? (
                  <>
                    <div className="px-3 py-2.5 grid gap-2 text-xs leading-[19px] text-pv-text-primary-text border-b border-b-pv-neutral-grey-100">
                      {isLoading ? (
                        <ViewDrillDownsLoader.ColumnInfo />
                      ) : (
                        <>
                          <div className="grid gap-1">
                            <div className="text-pv-text-secondary-text font-medium">
                              Name
                            </div>
                            <div>
                              {tables[tableIndex]?.columns[columnIndex]
                                ?.columnLabel ||
                                tables[tableIndex]?.columns[columnIndex]
                                  ?.columnName}
                            </div>
                          </div>
                          <div className="grid gap-1">
                            <div className="text-pv-text-secondary-text font-medium">
                              Description
                            </div>
                            <Tooltip
                              title={
                                tables[tableIndex]?.columns[columnIndex]
                                  ?.description
                              }
                              placement="top"
                              displayTooltipOnOverflow={true}
                            >
                              <p className="line-clamp-2 break-words">
                                {
                                  tables[tableIndex]?.columns[columnIndex]
                                    ?.description
                                }
                              </p>
                            </Tooltip>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="grid grid-rows-[auto_1fr] overflow-hidden py-2.5">
                      <div className="text-pv-text-secondary-text font-medium text-xs leading-[19px] px-3">
                        Sample Data
                      </div>
                      <div className="mt-1 pl-2.5 pr-[3px] mr-1 overflow-y-scroll">
                        <>
                          {isLoading || isSampleDataLoading ? (
                            <ViewDrillDownsLoader.SampleData />
                          ) : (
                            <>
                              {Array.isArray(sampleData?.data) &&
                              sampleData.data.length > 0 ? (
                                <>
                                  {sampleData.data.map((data, index) => (
                                    <div
                                      key={index}
                                      className="py-1 px-2 text-sm text-pv-text-secondary-text border-b border-b-pv-neutral-grey-100 leading-8 truncate"
                                    >
                                      {data}
                                    </div>
                                  ))}
                                </>
                              ) : (
                                <div className="text-xs text-pv-text-secondary-text py-6 text-center">
                                  No sample data available.
                                </div>
                              )}
                            </>
                          )}
                        </>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-pv-text-secondary-text text-sm">
                    No data available.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
